import { useUrlSearchParams, useLocalStorage } from '@vueuse/core';
import clone from 'lodash-es/clone';
import cloneDeep from 'lodash-es/cloneDeep';
import findIndex from 'lodash-es/findIndex';
import forEach from 'lodash-es/forEach';
import { defineStore } from 'pinia';
import { nextTick, unref, toRaw } from 'vue';

import useLiferayLocale from '../../../composables/useLiferayLocale';
import emitter from '../../../setup/bus';
import { SEARCH } from '../config';
import getParameterHeadings from '../helpers/getParameterHeadings';
import isRangeType from './helpers/isRangeType';

export const useSearchStore = defineStore('search', {
    state: () => ({
        /** @type boolean */
        busy: false,
        /** @type boolean */
        compareVisible: false,
        /** @type Number */
        currentPage: 1,
        /** @type RemovableRef<boolean> */
        expandedView: useLocalStorage('pinia/search/expandedView', true),
        /** @type SearchFacet[] */
        facets: [],
        /** @type boolean */
        facetsListOpen: false,
        /** @type NewsHubResult[] */
        featuredResults: [],
        /** @type {Object<string, boolean>} */
        features: {
            compare: true,
            csvExport: true,
            scrollTopOnPageChange: true,
            showAvailability: true,
            showBuyButton: true,
        },
        /** @type any[] */
        filters: [],
        /** @type boolean */
        initialSearchDone: false,
        /** @type {IntersectionObserver || null} */
        observer: null,
        /** @type boolean */
        productSearchMode: false,
        productTypes: [],
        /** @type {Object<string, Number[]>} */
        rangeSliders: {},
        /** @type boolean */
        reachedTheEnd: false,
        /** @type {SearchResult[] | NewsHubResult[]} */
        results: [],
        /** @type {string | null} */
        searchQuery: null,
        /** @type SearchFacet[] */
        selectedFacets: [],
        /** @type String[] */
        selectedNewsApplications: [],
        /** @type String[] */
        selectedNewsCategories: [],
        /** @type String[] */
        selectedNewsTypes: [],
        /** @type {Number[] | String[]} */
        selectedNewsYears: [],
        /** @type SearchResult[] */
        selectedProducts: [],
        /** @type {string | null} */
        sortFieldName: null,
        /** @type {'ASC' | 'DESC'} */
        sortOrder: 'ASC',
        /** @type {HTMLElement | null} */
        theEndRef: null,
        /** @type Number */
        totalResults: 0,
        /** @type boolean */
        userDecidedOnSearchMode: false,
    }),

    getters: {
        /**
         * @param {SearchResult[]} selectedProducts
         * @returns {Array}
         */
        groupedParametersForSelectedProducts({ selectedProducts }) {
            return getParameterHeadings(toRaw(selectedProducts), true);
        },

        /**
         * @param results
         * @returns {Array}
         */
        groupedPrimaryParameters({ results }) {
            return getParameterHeadings(toRaw(results), false);
        },

        /**
         * @param {SearchResult[]} results
         * @returns {boolean}
         */
        hasParameterInResults({ results }) {
            return !!results.some(r => r.parameters?.length > 0);
        },

        /**
         * @param {SearchFacet[]} productTypes1
         * @returns {boolean}
         */
        hasProductType1Selected({ productTypes1 }) {
            return !!productTypes1.some(facet => !!facet.isSelected);
        },

        /**
         * @param {SearchFacet[]} productTypes2
         * @returns {boolean}
         */
        hasProductType2Selected({ productTypes2 }) {
            return !!productTypes2.some(facet => !!facet.isSelected);
        },

        /**
         * @param {SearchResult[]} results
         * @returns {boolean}
         */
        hasResults({ results }) {
            return !!(results && results.length);
        },

        /**
         * @param {boolean} compareVisible
         * @returns {boolean}
         */
        isCompareVisible({ compareVisible }) {
            return !!compareVisible;
        },

        /**
         * @param {boolean} expandedView
         * @param {boolean} shouldOfferExpandedView
         * @param {boolean} isProductSearchMode
         * @returns {boolean}
         */
        isExpandedView({ expandedView, shouldOfferExpandedView, isProductSearchMode }) {
            return shouldOfferExpandedView === true && isProductSearchMode === true && expandedView === true;
        },

        /**
         * @param {string | boolean} productSearchMode
         * @returns {boolean}
         */
        isProductSearchMode({ productSearchMode }) {
            return productSearchMode === true || productSearchMode === 'true';
        },

        nextPage({ currentPage }) {
            return currentPage + 1;
        },

        /**
         * @param {SearchResult[]} selectedProducts
         * @returns {number}
         */
        numberOfSelectedProducts({ selectedProducts }) {
            return Array.isArray(selectedProducts) ? selectedProducts.length : 0;
        },

        /**
         * @param {SearchFacet[]} facets
         * @returns {SearchFacet[] | []}
         */
        productTypes1({ facets }) {
            return facets?.find(facet => facet.id === 'products')?.children || [];
        },

        /**
         * @param {SearchFacet[]} productTypes1
         * @returns {SearchFacet[] | []}
         */
        productTypes2({ productTypes1 }) {
            return productTypes1.length === 1 ? productTypes1?.[0].children : [];
        },

        /**
         * @param {SearchResult[]} results
         * @returns {boolean}
         */
        shouldDisplayProductFamilyColumn({ results }) {
            return !!results?.some(r => Object.hasOwn(r, 'familyHeadline') && r.familyHeadline !== '');
        },

        /**
         * @param {boolean} hasProductType2Selected
         * @param {boolean} isProductSearchMode
         * @param {boolean} hasParameterInResults
         * @returns {boolean}
         */
        shouldOfferExpandedView({ isProductSearchMode, productTypes2, hasParameterInResults }) {
            return (
                isProductSearchMode === true &&
                productTypes2 &&
                productTypes2.length === 1 &&
                hasParameterInResults === true
            );
        },

        /**
         * @param {boolean} busy
         * @param {SearchFacet[]} facets
         * @param {boolean} productSearchMode
         * @param {string} searchQuery
         * @param {boolean} userDecidedOnSearchMode
         * @returns {boolean}
         */
        shouldOfferProductSearch({ busy, facets, productSearchMode, searchQuery, userDecidedOnSearchMode }) {
            if (busy || productSearchMode) {
                return false;
            }

            if (userDecidedOnSearchMode && !productSearchMode) {
                return false;
            }

            if (searchQuery && searchQuery.length === 0 && !productSearchMode) {
                return true;
            }

            if (searchQuery === null && !productSearchMode) {
                return true;
            }

            // noinspection RedundantIfStatementJS
            if (
                searchQuery !== null &&
                searchQuery.length > 2 &&
                facets?.length &&
                facets?.[0]?.children?.some(o => o.id === 'amsosramproduct')
            ) {
                return true;
            }

            return false;
        },
    },

    actions: {
        /**
         * Recursive function removing all child facets of facet from selectedFacets.
         *
         * @private
         * @param {SearchFacet[]} selectedFacets
         * @param {SearchFacet} facet
         * @returns {SearchFacet[]}
         */
        _deselectChildFacets(selectedFacets, facet) {
            unref(facet).children?.forEach(childFacet => {
                const selectedIndex = findIndex(selectedFacets, { id: childFacet.id });
                if (selectedIndex !== -1) {
                    selectedFacets.splice(selectedIndex, 1);

                    if (Object.hasOwn(childFacet, 'children') && childFacet.children.length) {
                        selectedFacets = this._deselectChildFacets(selectedFacets, childFacet);
                    }

                    if (childFacet.type === 'assetCategoryIds') {
                        this.resetRangeSliders();
                    }
                }
            });

            return selectedFacets;
        },

        /**
         * Recursive mark child facets as selected.
         *
         * @private
         * @param {SearchFacet} parent
         */
        _markSelected(parent) {
            parent.children.forEach(facet => {
                facet.isSelected =
                    findIndex(
                        this.selectedFacets,
                        o => o.name === facet.name && o.id === facet.id && o.type === facet.type
                    ) !== -1;

                if (this.hasFilter('products', facet.key)) {
                    facet.selected = true;
                }

                if (facet.selected && !facet.isSelected) {
                    facet.isSelected = true;
                    this.selectedFacets.push(facet);
                }
                this._markSelected(facet);
            });
        },

        /**
         * @private
         * @param {SearchFacet} parent
         * @param {string} parentKey
         */
        _setParentKey(parent, parentKey) {
            parent.children.forEach(facet => {
                facet.parentKey = parentKey;
                this._setParentKey(facet, parentKey);
            });
        },

        /**
         * Toggle passed facet's selection status (implementation).
         *
         * @private
         * @param {SearchFacet} facet
         * @param {boolean} forceRemoval
         * @param {boolean} updateIfExists
         */
        _toggleSelectedFacet(facet, forceRemoval = false, updateIfExists = false) {
            const index = findIndex(this.selectedFacets, { id: unref(facet).id });

            if (index !== -1 && updateIfExists === false) {
                let _selectedFacets = cloneDeep(this.selectedFacets);
                _selectedFacets.splice(index, 1);

                if (Object.hasOwn(unref(facet), 'children') && unref(facet).children.length) {
                    _selectedFacets = this._deselectChildFacets(_selectedFacets, facet);
                }

                if (unref(facet).type === 'assetCategoryIds') {
                    //_selectedFacets = _selectedFacets.filter(o => !o.type.startsWith('param_'));
                    this.filters = [];
                }

                this.selectedFacets = _selectedFacets;

                if (unref(facet).id === 'products') {
                    this.resetRangeSliders();
                }

                if (forceRemoval === true && Object.hasOwn(this.rangeSliders, facet.id)) {
                    delete this.rangeSliders[facet.id];
                }
            } else if (index !== -1 && updateIfExists === true) {
                let _selectedFacets = cloneDeep(this.selectedFacets);
                _selectedFacets[index] = facet;

                this.selectedFacets = _selectedFacets;
            } else if (forceRemoval === false) {
                this.selectedFacets.push(unref(facet));
            }
        },

        /** @param {SearchFacet} filter */
        addFilter(filter) {
            this.filters.push(filter);
        },

        /**
         * Toggle passed facets selection status.
         *
         * @fires search:facets-updated
         * @param {SearchFacet[]} facets
         * @returns {void}
         */
        batchToggleSelectedFacets(facets) {
            const listOfFacets = [...facets];
            listOfFacets.forEach(facet => this._toggleSelectedFacet(facet));
            emitter.emit('search:facets-updated');
        },

        /**
         * Clear all relevant filters.
         *
         * @returns Void
         */
        clearFilter() {
            this.$patch({
                filters: [],
                selectedFacets: [],
                rangeSliders: {},
            });

            emitter.emit('search:filters-cleared');
        },

        /**
         * Extract, cleanup and sort facets from a search response.
         *
         * @param {SearchFacet[]} rawFacets
         */
        extractFacets(rawFacets) {
            const newFacets = [];
            forEach([...rawFacets].slice(), facet => {
                if (facet.children?.length && facet.id !== null) {
                    newFacets.push(facet);
                    this._markSelected(facet);
                    this._setParentKey(facet, facet.key);
                }
            });

            // Initialize the browser's Intl API for sorting the parameter facets later.
            const { languageKey } = useLiferayLocale();
            const collator = new Intl.Collator(unref(languageKey), {
                numeric: true,
                sensitivity: 'base',
            });

            // Split default and parameter facets so we can sort parameter facets alphabetically
            const defaultFacets = newFacets.filter(
                f => !f.key.startsWith('param-') && !f.children?.some(c => c.key.startsWith('param-'))
            );
            const parameterFacets = newFacets
                .filter(f => !defaultFacets.includes(f))
                .sort((a, b) => collator.compare(a.name, b.name));

            // Find the "Color and wavelength" facet so we can sort its children
            const index = findIndex(parameterFacets, f => f.id === 'param_color-of-emission_String_sortable');
            if (index !== -1) {
                parameterFacets[index].children = parameterFacets[index].children.sort(
                    (a, b) =>
                        SEARCH.COLOR_WAVELENGTH_SORT_ORDER.indexOf(a.name) -
                        SEARCH.COLOR_WAVELENGTH_SORT_ORDER.indexOf(b.name)
                );
            }

            // Return concatenated facets
            return [].concat(defaultFacets, parameterFacets);
        },

        /**
         * Extract range slider values.
         *
         * @param {SearchFacet[]} rawAggregations
         * @returns {{ [key: String]: [Number, Number] }}
         */
        extractRangeSliderValues(rawAggregations) {
            const validFacets = rawAggregations.filter(
                facet => facet && facet.id !== null && Array.isArray(facet.children) && facet.children.length
            );

            const rangeSliderValues = {};
            const additionalSelectedFacets = [];

            // Extract flat range slider values
            const selectedRangeSliderFacets = validFacets.filter(
                f =>
                    f.key?.startsWith('param-') &&
                    f.selected &&
                    isRangeType(f.type) &&
                    'minValue' in f &&
                    'maxValue' in f
            );
            selectedRangeSliderFacets.forEach(f => {
                rangeSliderValues[f.id] = [f.minValue, f.maxValue];
                const index = findIndex(this.selectedFacets, s => s.id === f.id);
                if (index === -1) {
                    additionalSelectedFacets.push({
                        ...f,
                        isSelected: true,
                    });
                }
            });

            // Extracted grouped range slider values
            const groupedRangeSliderFacets = validFacets
                .filter(
                    f =>
                        f.selected &&
                        'grouping' in f &&
                        isRangeType(f.type) &&
                        Array.isArray(f.children) &&
                        f.children.some(c => c.selected)
                )
                .flatMap(f =>
                    f.children.filter(
                        c =>
                            c.selected &&
                            'minValue' in c &&
                            'maxValue' in c &&
                            c.minValue !== null &&
                            c.maxValue !== null
                    )
                );
            groupedRangeSliderFacets.forEach(f => {
                rangeSliderValues[f.id] = [f.minValue, f.maxValue];
            });

            if (additionalSelectedFacets.length) {
                this.selectedFacets = [].concat(this.selectedFacets, additionalSelectedFacets);
            }

            return rangeSliderValues;
        },

        /** @returns {NewsFilters} */
        getNewsFilters(...mandatoryNewsFilters) {
            const filters = [];

            const addFilters = (key, values) => {
                values.filter(Boolean).forEach(value => {
                    filters.push({
                        filterKey: key,
                        filterValue: value,
                    });
                });
            };

            if (mandatoryNewsFilters.length) {
                addFilters(
                    'news',
                    mandatoryNewsFilters[0].map(filter => filter.key)
                );
            } else {
                addFilters('news', this.selectedNewsTypes);
            }
            addFilters('news-hub', this.selectedNewsCategories);
            addFilters('applications', this.selectedNewsApplications);

            return filters;
        },

        /**
         * @param {String} id
         * @returns {(Number, Number)[]|null}
         */
        getRangeSliderValues(id) {
            return Object.hasOwn(this.rangeSliders, id) ? this.rangeSliders[id] : null;
        },

        /**
         * @param {String} key
         * @param {String} value
         * @returns {boolean}
         */
        hasFilter(key, value) {
            return findIndex(this.filters, o => o.filterKey === key && o.filterValue === value) !== -1;
        },

        initializeObserver() {
            this.reachedTheEnd = false;
            this.observer = new IntersectionObserver(entries => {
                entries.forEach(
                    entry => {
                        if (!this.reachedTheEnd && !this.busy && entry.isIntersecting) {
                            nextTick(() => {
                                emitter.emit('search:news:reached-the-end');
                            });
                        }
                    },
                    {
                        rootMargin: '200% 0px 0px 0px',
                    }
                );
            });

            this.observer.observe(this.theEndRef);
        },

        /** @returns {SearchAggregation[]} */
        prepareAggregationsForPayload() {
            /** @type SearchAggregation[] */
            const aggregations = [];
            /** @type SearchFacet[] */
            const _selectedFacets = cloneDeep(this.selectedFacets);

            if (this.initialSearchDone === false && (!_selectedFacets || !_selectedFacets.length)) {
                const params = useUrlSearchParams('history');
                for (const [key, value] of Object.entries(params)) {
                    let field = key.replace('filter_', '');
                    let values = value.split(',');

                    if (key === 'filter_param_color-of-emission_String_sortable') {
                        for (let value of values) {
                            aggregations.push({
                                type: 'text',
                                field,
                                value,
                            });
                        }
                    } else if (key.startsWith('filter_param_')) {
                        values = value.split(':');
                        if (values.length === 1) {
                            aggregations.push({
                                type: 'named',
                                field,
                                value,
                            });
                        } else {
                            aggregations.push({
                                type: 'range',
                                field,
                                minValue: parseFloat(values[0]),
                                maxValue: parseFloat(values[1]),
                            });
                        }
                    } else if (key.startsWith('filter_')) {
                        for (let value of values) {
                            aggregations.push({
                                type: 'named',
                                field,
                                value,
                            });
                        }
                    }
                }

                this.initialSearchDone = true;

                return aggregations;
            }

            _selectedFacets.forEach(facet => {
                if (facet.type === 'vocabularies' && facet.children.length === 0) {
                    return;
                }

                if (facet.type === 'assettypes' && facet.children.length === 0) {
                    return;
                }

                if (facet.id === null) {
                    return;
                }

                if (isRangeType(facet.type) && Object.hasOwn(facet, 'minValue') && Object.hasOwn(facet, 'maxValue')) {
                    aggregations.push({
                        type: 'range',
                        field: facet.id,
                        minValue: facet.minValue,
                        maxValue: facet.maxValue,
                    });
                } else {
                    aggregations.push({
                        type: 'term',
                        field: facet.type,
                        value: facet.id,
                    });
                }
            });

            return aggregations;
        },

        /**
         * @param {String} id
         * @param {Number} min
         * @param {Number} max
         */
        registerRangeSlider(id, min, max) {
            this.rangeSliders[id] = [min, max];
        },

        /**
         * @fires search:news:filter-updated
         * @returns Void
         */
        resetNewsCategoryAndApplicationFilters() {
            this.$patch({
                selectedNewsCategories: [],
                selectedNewsApplications: [],
            });

            emitter.emit('search:news:filter-updated');
        },

        /**
         * @fires search:news:filter-updated
         * @returns Void
         */
        resetNewsTypeFilter() {
            this.selectedNewsTypes = [];

            emitter.emit('search:news:filter-updated');
        },

        /**
         * @fires search:news:filter-updated
         * @returns Void
         */
        resetNewsYearFilter() {
            this.selectedNewsYears = [];
            emitter.emit('search:news:filter-updated');
        },

        resetObserver() {
            this.stopObserving();
            this.initializeObserver();
        },

        /**
         * Reset range slider values.
         *
         * @returns Void
         */
        resetRangeSliders() {
            this.rangeSliders = {};
        },

        resetSearch() {
            this.$patch({
                filters: [],
                rangeSliders: {},
                results: [],
                searchQuery: null,
                selectedFacets: [],
            });

            emitter.emit('search:reset');
        },

        /** @param {String} fieldName */
        setSortField(fieldName) {
            if (this.sortFieldName === fieldName) {
                if (this.sortOrder === 'DESC') {
                    this.sortFieldName = null;
                } else {
                    this.sortOrder = 'DESC';
                }
            } else {
                this.sortFieldName = fieldName;
                this.sortOrder = 'ASC';
            }
            emitter.emit('search:sorting-updated');
        },

        stopObserving() {
            this.reachedTheEnd = true;
            if (this.observer !== null) {
                this.observer.disconnect();
            }
        },

        toggleCompareVisible() {
            const params = useUrlSearchParams('history');

            this.compareVisible = !this.compareVisible;

            params.compareVisible = this.compareVisible ? 'true' : null;
        },

        /**
         * Toggles the facets list.
         *
         * @param {boolean} isTriggeredFromUpdateFilter
         * @returns {void}
         */
        toggleFacetsListOpen(isTriggeredFromUpdateFilter = false) {
            if (isTriggeredFromUpdateFilter) {
                this.facetsListOpen = false;
            } else {
                this.facetsListOpen = !this.facetsListOpen;
            }
            if (this.facetsListOpen === true) {
                document.body.classList.add('has-scrolllock');
            } else {
                document.body.classList.remove('has-scrolllock');
            }
        },

        /**
         * @fires search:news:filter-updated
         * @param {string} key
         */
        toggleNewsApplicationFilter(key) {
            const index = this.selectedNewsApplications.indexOf(key);

            if (index === -1) {
                this.selectedNewsApplications.push(key);
            } else {
                this.selectedNewsApplications.splice(index, 1);
            }

            emitter.emit('search:news:filter-updated');
        },

        /**
         * @fires search:news:filter-updated
         * @param {string} key
         */
        toggleNewsCategoryFilter(key) {
            const index = this.selectedNewsCategories.indexOf(key);

            if (index === -1) {
                this.selectedNewsCategories.push(key);
            } else {
                this.selectedNewsCategories.splice(index, 1);
            }

            emitter.emit('search:news:filter-updated');
        },

        /**
         * @fires search:news:filter-updated
         * @param {string} key
         */
        toggleNewsTypeFilter(key) {
            const index = this.selectedNewsTypes.indexOf(key);

            if (index === -1) {
                this.selectedNewsTypes.push(key);
            } else {
                this.selectedNewsTypes.splice(index, 1);
            }

            emitter.emit('search:news:filter-updated');
        },

        /**
         * @fires search:news:filter-updated
         * @param {number | string} year
         * @returns Void
         */
        toggleNewsYearFilter(year) {
            if (year === 'archive') {
                if (this.selectedNewsYears.includes(year)) {
                    this.selectedNewsYears = [];
                } else {
                    this.selectedNewsYears = [];
                    this.selectedNewsYears.push(year);
                }
            } else {
                if (this.selectedNewsYears.includes('archive')) {
                    this.selectedNewsYears = [];
                }
                if (this.selectedNewsYears.includes(year)) {
                    const index = this.selectedNewsYears.indexOf(year);
                    this.selectedNewsYears.splice(index, 1);
                } else {
                    this.selectedNewsYears.push(year);
                }
            }
            emitter.emit('search:news:filter-updated');
        },

        /**
         * Toggles the search mode.
         *
         * @fires search:mode-updated
         * @param {boolean | null} forceStateTo Forces the search to product/parametric mode if set to true or to
         *   default mode if set to false.
         * @param {boolean} remember Will save the user's choice in the state.
         * @param {boolean} skipFilterReset Skip resetting the filters.
         * @param {boolean} silent Change silently and do not fire @search:mode-updated
         * @returns {void}
         */
        toggleProductSearchMode(forceStateTo = null, remember = false, skipFilterReset = false, silent = false) {
            if (forceStateTo !== null) {
                this.productSearchMode = !!forceStateTo;
            } else {
                this.productSearchMode = !this.productSearchMode;
            }

            if (remember === true) {
                this.userDecidedOnSearchMode = true;
            }

            if (skipFilterReset !== true) {
                this.clearFilter();
            }

            if (silent === false) {
                this.updateUrlParameters();
                emitter.emit('search:mode-updated');
            }
        },

        /**
         * Toggle passed facet's selection status.
         *
         * @param {SearchFacet} facet
         * @param {boolean} forceRemoval
         * @param {boolean} updateIfExists
         */
        toggleSelectedFacet(facet, forceRemoval = false, updateIfExists = false) {
            this._toggleSelectedFacet(facet, forceRemoval, updateIfExists);
            emitter.emit('search:facets-updated');
        },

        /** @param {SearchResult} product */
        toggleSelectedProduct(product) {
            const productName = unref(product).name;
            const selectedProductsIndex = findIndex(this.selectedProducts, { name: productName });

            if (selectedProductsIndex !== -1) {
                this.selectedProducts.splice(selectedProductsIndex, 1);
            } else {
                this.selectedProducts.push(unref(product));
            }
        },

        /** @returns {void} */
        updateNewsUrlParameters() {
            const _types = clone(this.selectedNewsTypes);
            const _categories = clone(this.selectedNewsCategories);
            const _applications = clone(this.selectedNewsApplications);
            const _years = clone(this.selectedNewsYears);

            const params = useUrlSearchParams('history');
            Object.keys(params).forEach(filterParam => (params[filterParam] = null));

            params.news = _types && _types.length ? _types.join(',') : null;
            params['news-hub'] = _categories && _categories.length ? _categories.join(',') : null;
            params.applications = _applications && _applications.length ? _applications.join(',') : null;
            params.year = _years && _years.length ? _years.join(',') : null;
            params.archive = _years && _years.includes(SEARCH.NEWS_YEAR_ARCHIVE) ? true : null;
        },

        /** @returns {void} */
        updateUrlParameters() {
            const _selectedFacets = cloneDeep(this.selectedFacets);

            const params = useUrlSearchParams('history');
            Object.keys(params)
                .filter(param => param.startsWith('filter_'))
                .forEach(filterParam => (params[filterParam] = null));

            params.q = this.searchQuery && this.searchQuery.length ? this.searchQuery : null;

            params.productSearch = this.isProductSearchMode ? 'true' : null;

            _selectedFacets.forEach(facet => {
                if (facet.type === 'assetCategoryIds') {
                    const key = `filter_${facet.parentKey}`;
                    if (facet.parentKey !== 'products' && Object.hasOwn(params, key) && params[key]) {
                        params[key] = `${params[key]},${facet.key}`;
                    } else {
                        params[key] = facet.key;
                    }
                } else if (
                    facet.type === 'family_name' ||
                    facet.type === 'param_color-of-emission_String_sortable' ||
                    facet.type === 'lifecycle_key_String_sortable'
                ) {
                    const key = `filter_${facet.type}`;
                    if (Object.hasOwn(params, key) && params[key]) {
                        params[key] = `${params[key]},${facet.id}`;
                    } else {
                        params[key] = facet.id;
                    }
                } else if (isRangeType(facet.type) && facet.key.startsWith('param-')) {
                    const key = `filter_${facet.id}`;
                    params[key] = `${facet.minValue}:${facet.maxValue}`;
                } else {
                    params[`filter_${facet.type}`] = facet.id;
                }
            });
        },
    },
});
